import type { AuthedRequester } from "@/lib/brokerAuth";
import type { BondProgramType } from "@/lib/brokerBonds";

// BRK-S412 — the Surety account's Actions menu: Send application.
//
// SEND APPLICATION has PMS email the insured the Signal Express or
// Signal Advance application (PMS-S840, D4). Pinned since BRK-S511: the body
// and the 202 are mirrored in `test/contract/bondSeamMirror.ts`. A bad address
// is a 422 VALIDATION_FAILED on `email`, a suppressed one 422
// RECIPIENT_UNDELIVERABLE — both thrown for the dialog to render.

/** The applications PMS can send — the body's `program` enum (PMS-S840). */
export const APPLICATION_PROGRAMS = [
  "express",
  "advance",
] as const satisfies readonly BondProgramType[];
export type ApplicationProgram = (typeof APPLICATION_PROGRAMS)[number];

export const APPLICATION_PROGRAM_LABELS: Record<ApplicationProgram, string> = {
  express: "Signal Express application",
  advance: "Signal Advance application",
};

/**
 * Which applications the dialog offers, given the program the insured HOLDS
 * (null for none). Signal Advance is offered to an insured on Express or on no
 * program; one already on Advance has nothing to move up to.
 */
export function applicationProgramOptions(
  held: BondProgramType | null,
): ApplicationProgram[] {
  return held === "advance" ? ["express"] : ["express", "advance"];
}

export interface ApplicationSendInput {
  program: ApplicationProgram;
  email: string;
  /** Optional; a blank note is left off the wire. */
  note?: string;
}

/** What PMS answers once the email is queued (202, PMS-S840). */
export interface ApplicationSendReceipt {
  program: ApplicationProgram;
  email: string;
  sentAt: string;
}

/** POST /api/broker/accounts/{id}/applications/send (PMS-S840). */
export function sendClientApplication(
  request: AuthedRequester,
  accountId: string,
  input: ApplicationSendInput,
): Promise<ApplicationSendReceipt> {
  const note = input.note?.trim();
  return request<ApplicationSendReceipt>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/applications/send`,
    {
      method: "POST",
      body: {
        program: input.program,
        email: input.email,
        ...(note ? { note } : {}),
      },
    },
  );
}

/** The dialog's confirmation, said only once PMS answered 2xx: the recipient
 *  the receipt names, else the one that was sent. */
export function applicationSentLine(
  receipt: Partial<ApplicationSendReceipt> | null | undefined,
  sentTo: string,
): string {
  return `Application sent to ${echoedEmail(receipt, sentTo)}`;
}

// BRK-S405 — SEND PRE-QUALIFICATION LETTER, from the account's program buttons
// and the submission's Surety Program tab: PMS emails a letter confirming the
// insured is pre-qualified under the program it holds, to whoever the broker
// names (an obligee, a GC), optionally naming the obligee on it. Each send
// mints a new version of the account's `prequalification_letter` document
// (PMS-S847), pinned since BRK-S511. 409 NO_PROGRAM_IN_FORCE when the insured
// holds no program; the address refusals are the application send's.

export interface PrequalificationSendInput {
  email: string;
  /** Optional; a blank obligee is left off the wire. */
  obligeeName?: string;
}

/** What PMS answers once the letter is queued (202, PMS-S847); `obligeeName`
 *  is absent when none was named. */
export interface PrequalificationSendReceipt {
  email: string;
  obligeeName?: string;
  sentAt: string;
}

/** POST /api/broker/accounts/{id}/prequalification/send (PMS-S847). */
export function sendPrequalificationLetter(
  request: AuthedRequester,
  accountId: string,
  input: PrequalificationSendInput,
): Promise<PrequalificationSendReceipt> {
  const obligeeName = input.obligeeName?.trim();
  return request<PrequalificationSendReceipt>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/prequalification/send`,
    {
      method: "POST",
      body: {
        email: input.email,
        ...(obligeeName ? { obligeeName } : {}),
      },
    },
  );
}

/** The letter dialog's confirmation, said only once PMS answered 2xx: the
 *  recipient the receipt names, else the one that was sent. */
export function prequalificationSentLine(
  receipt: Partial<PrequalificationSendReceipt> | null | undefined,
  sentTo: string,
): string {
  return `Sent to ${echoedEmail(receipt, sentTo)}`;
}

function echoedEmail(
  receipt: { email?: string } | null | undefined,
  sentTo: string,
): string {
  return typeof receipt?.email === "string" && receipt.email.trim() !== ""
    ? receipt.email
    : sentTo;
}
